'use client';

import { useState, ReactNode, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { User, Menu, ChevronDown } from 'lucide-react';
import { HttpTypes } from '@medusajs/types';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { JinMobileMenu } from '../header-showcase/jin-mobile-menu';
import { FunStoreMobileMenu } from './fun-store-mobile-menu';
import { useTenant, useTenantAndVariant } from '@multitenancy/TenantAndVariantContext';
import { FUN_STORE_NAV } from './fun-store-nav-config';
import InlineSearchBar from './inline-search-bar';

interface HeaderClassicProps {
  regions: HttpTypes.StoreRegion[];
  collections: HttpTypes.StoreCollection[];
  categories: HttpTypes.StoreProductCategory[];
  cartButton: ReactNode;
}

/**
 * Build nav items dynamically from categories.
 * Root categories with children become dropdowns.
 * Root categories without children become simple links (if marked nav_visible).
 */
function buildNavCategories(categories: HttpTypes.StoreProductCategory[]) {
  // Get root categories (no parent)
  const roots = categories.filter(c => !c.parent_category_id);
  
  const megaMenus: { parent: HttpTypes.StoreProductCategory; children: any[] }[] = [];
  const dropdowns: { parent: HttpTypes.StoreProductCategory; children: HttpTypes.StoreProductCategory[] }[] = [];
  const simpleLinks: HttpTypes.StoreProductCategory[] = [];

  for (const cat of roots) {
    const children = (cat.category_children || []) as HttpTypes.StoreProductCategory[];
    if (children.length === 0) {
      // Simple link — only show if metadata.nav_visible is true
      if ((cat as any).metadata?.nav_visible) {
        simpleLinks.push(cat);
      }
      continue;
    }
    
    // Check if any child has grandchildren (mega menu) or just children (dropdown)
    const hasGrandchildren = children.some((child: any) => 
      (child.category_children || []).length > 0
    );
    
    if (hasGrandchildren) {
      megaMenus.push({
        parent: cat,
        children: children.map((child: any) => ({
          ...child,
          children: child.category_children || [],
        })),
      });
    } else {
      dropdowns.push({ parent: cat, children });
    }
  }

  return { megaMenus, dropdowns, simpleLinks };
}

export function HeaderClassic({ regions, collections, categories, cartButton }: HeaderClassicProps) {
  const storeConfig = useTenant();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { featureToggles } = useTenantAndVariant();

  const { megaMenus, dropdowns, simpleLinks } = useMemo(() => buildNavCategories(categories), [categories]);
  const isFunStore = storeConfig.sourceStore === 'chokyo';

  return (
    <>
      <header className="sticky top-0 z-50 bg-white">
        {/* 1. Announcement Bars */}
        {featureToggles.showAnnouncementBar1 && (
          <div className="bg-primary text-white text-center py-2 text-xs tracking-wider font-medium">
            <p>Welcome to {storeConfig.displayName}</p>
          </div>
        )}
        {featureToggles.showAnnouncementBar2 && (
          <div className="bg-secondary text-foreground text-center py-2 text-xs tracking-wider font-medium">
            <p>Don&apos;t Miss Our Grand Opening Special! Shop Now for Great Deals!</p>
          </div>
        )}
        {featureToggles.showAnnouncementBar3 && (
          <div className="bg-tertiary text-foreground text-center py-2 text-xs tracking-wider font-medium">
            <p>Free shipping on orders $150+</p>
          </div>
        )}

        {/* 2. Single Bar with Logo Left, Navigation Center, Icons Right */}
        <div className="border-b border-gray-200 bg-white">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-8">
            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2 hover:text-[var(--primary)] transition-colors"
                aria-label="Open menu"
              >
                <Menu size={24} />
              </button>
            </div>

            {/* Logo on Left */}
            <LocalizedClientLink href="/" className="flex items-center shrink-0">
              {storeConfig.name === 'fun' ? (
                <span className="flex items-center gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="var(--primary)" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 18.5c-2.5 2.5-6 1-6-2 0-2 2.5-4.5 6-4.5s6 2.5 6 4.5c0 3-3.5 4.5-6 2z" />
                    <ellipse cx="7" cy="8" rx="2.5" ry="3" />
                    <ellipse cx="17" cy="8" rx="2.5" ry="3" />
                    <ellipse cx="4" cy="13" rx="2" ry="2.5" />
                    <ellipse cx="20" cy="13" rx="2" ry="2.5" />
                  </svg>
                  <span className="text-xl md:text-2xl font-bold tracking-tight" style={{ color: 'var(--primary)' }}>
                    Pet<span style={{ color: 'var(--secondary)' }}>Select</span>
                  </span>
                </span>
              ) : (
                <Image
                  src={`/images/logo_${storeConfig.name}_horizontal.png`}
                  alt={storeConfig.displayName}
                  width={160}
                  height={48}
                  className="h-10 md:h-12 w-auto"
                  priority
                />
              )}
            </LocalizedClientLink>

            {/* Navigation Inline (Desktop Only) */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8 text-xs xl:text-sm font-bold tracking-widest uppercase text-[var(--muted)] flex-1 justify-center">
              <LocalizedClientLink href="/" className="hover:text-[var(--foreground)] transition-colors">
                Home
              </LocalizedClientLink>

              {isFunStore ? (
                <>
                  {/* Fun Store Mega Menus (Cat, Dog) */}
                  {FUN_STORE_NAV.megaMenus.map((menu) => (
                    <div key={menu.handle} className="relative group">
                      <LocalizedClientLink
                        href={`/categories/${menu.handle}`}
                        className="hover:text-[var(--foreground)] transition-colors flex items-center gap-1"
                      >
                        {menu.label}
                        <ChevronDown size={14} className="group-hover:rotate-180 transition-transform" />
                      </LocalizedClientLink>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-gray-200 shadow-xl rounded-md py-6 px-8 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[700px]">
                        <LocalizedClientLink
                          href={`/categories/${menu.handle}`}
                          className="block mb-4 text-xs font-bold tracking-widest uppercase text-[var(--primary)] hover:underline"
                        >
                          All {menu.label}
                        </LocalizedClientLink>
                        <div className="grid grid-cols-4 gap-6">
                          {menu.groups.map((group) => (
                            <div key={group.label}>
                              {group.handle ? (
                                <LocalizedClientLink
                                  href={`/categories/${group.handle}`}
                                  className="block text-xs font-bold tracking-wider uppercase text-[var(--foreground)] mb-2 hover:text-[var(--primary)] transition-colors"
                                >
                                  {group.label}
                                </LocalizedClientLink>
                              ) : (
                                <span className="block text-xs font-bold tracking-wider uppercase text-[var(--foreground)] mb-2">
                                  {group.label}
                                </span>
                              )}
                              <ul className="space-y-1.5">
                                {group.items.map((item) => (
                                  <li key={item.handle}>
                                    <LocalizedClientLink
                                      href={`/categories/${item.handle}`}
                                      className="text-xs text-gray-500 hover:text-[var(--primary)] transition-colors"
                                    >
                                      {item.label}
                                    </LocalizedClientLink>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Fun Store Dropdowns (Small Animals) */}
                  {FUN_STORE_NAV.dropdowns.map((dropdown) => (
                    <div key={dropdown.handle} className="relative group">
                      <LocalizedClientLink
                        href={`/categories/${dropdown.handle}`}
                        className="hover:text-[var(--foreground)] transition-colors flex items-center gap-1"
                      >
                        {dropdown.label}
                        <ChevronDown size={14} className="group-hover:rotate-180 transition-transform" />
                      </LocalizedClientLink>
                      <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 shadow-lg rounded-md py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                        <LocalizedClientLink
                          href={`/categories/${dropdown.handle}`}
                          className="block px-4 py-2 text-xs font-bold tracking-wider uppercase text-[var(--primary)] hover:bg-gray-50 transition-colors"
                        >
                          All {dropdown.label}
                        </LocalizedClientLink>
                        {dropdown.items.map((item) => (
                          <LocalizedClientLink
                            key={item.handle}
                            href={`/categories/${item.handle}`}
                            className="block px-4 py-2 text-xs font-semibold tracking-wider hover:bg-gray-50 hover:text-[var(--foreground)] transition-colors"
                          >
                            {item.label}
                          </LocalizedClientLink>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Fun Store Simple Links (Brands) */}
                  {FUN_STORE_NAV.links.map((link) => (
                    <LocalizedClientLink key={link.href} href={link.href} className="hover:text-[var(--foreground)] transition-colors">
                      {link.label}
                    </LocalizedClientLink>
                  ))}
                </>
              ) : (
                <>
                  <LocalizedClientLink href="/store" className="hover:text-[var(--foreground)] transition-colors">
                    New Arrivals
                  </LocalizedClientLink>

                  {/* Dynamic Mega-Menus (categories with grandchildren) */}
                  {megaMenus.map((menu) => (
                    <div key={menu.parent.id} className="relative group">
                      <button className="hover:text-[var(--foreground)] transition-colors flex items-center gap-1">
                        {menu.parent.name}
                        <ChevronDown size={14} className="group-hover:rotate-180 transition-transform" />
                      </button>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-gray-200 shadow-xl rounded-md py-6 px-8 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[700px]">
                        <LocalizedClientLink
                          href={`/categories/${menu.parent.handle}`}
                          className="block mb-4 text-xs font-bold tracking-widest uppercase text-[var(--primary)] hover:underline"
                        >
                          All {menu.parent.name}
                        </LocalizedClientLink>
                        <div className="grid grid-cols-4 gap-6">
                          {menu.children.map((subGroup: any) => (
                            <div key={subGroup.id}>
                              <LocalizedClientLink
                                href={`/categories/${subGroup.handle}`}
                                className="block text-xs font-bold tracking-wider uppercase text-[var(--foreground)] mb-2 hover:text-[var(--primary)] transition-colors"
                              >
                                {subGroup.name}
                              </LocalizedClientLink>
                              {subGroup.children && subGroup.children.length > 0 && (
                                <ul className="space-y-1.5">
                                  <li>
                                    <LocalizedClientLink
                                      href={`/categories/${subGroup.handle}`}
                                      className="text-xs text-gray-500 hover:text-[var(--primary)] transition-colors"
                                    >
                                      All {subGroup.name}
                                    </LocalizedClientLink>
                                  </li>
                                  {subGroup.children.map((child: any) => (
                                    <li key={child.id}>
                                      <LocalizedClientLink
                                        href={`/categories/${child.handle}`}
                                        className="text-xs text-gray-500 hover:text-[var(--primary)] transition-colors"
                                      >
                                        {child.name}
                                      </LocalizedClientLink>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Dynamic Dropdowns */}
                  {dropdowns.map((dropdown) => (
                    <div key={dropdown.parent.id} className="relative group">
                      <button className="hover:text-[var(--foreground)] transition-colors flex items-center gap-1">
                        {dropdown.parent.name}
                        <ChevronDown size={14} className="group-hover:rotate-180 transition-transform" />
                      </button>
                      <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 shadow-lg rounded-md py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                        <LocalizedClientLink
                          href={`/categories/${dropdown.parent.handle}`}
                          className="block px-4 py-2 text-xs font-bold tracking-wider uppercase text-[var(--primary)] hover:bg-gray-50 transition-colors"
                        >
                          All {dropdown.parent.name}
                        </LocalizedClientLink>
                        {dropdown.children.map((child: any) => (
                          <LocalizedClientLink
                            key={child.id}
                            href={`/categories/${child.handle}`}
                            className="block px-4 py-2 text-xs font-semibold tracking-wider hover:bg-gray-50 hover:text-[var(--foreground)] transition-colors"
                          >
                            {child.name}
                          </LocalizedClientLink>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Simple category links */}
                  {simpleLinks.map((cat) => (
                    <LocalizedClientLink key={cat.id} href={`/categories/${cat.handle}`} className="hover:text-[var(--foreground)] transition-colors">
                      {cat.name}
                    </LocalizedClientLink>
                  ))}
                </>
              )}

              <LocalizedClientLink href="/store" className="hover:text-[var(--foreground)] transition-colors">
                Shop All
              </LocalizedClientLink>
            </nav>

            {/* Icons on Far Right */}
            <div className="flex items-center space-x-4 md:space-x-6 shrink-0">
              <InlineSearchBar />
              <LocalizedClientLink
                href="/account"
                aria-label="Account"
                className="hover:text-[var(--primary)] transition-colors hidden sm:block"
              >
                <User size={20} />
              </LocalizedClientLink>
              {cartButton}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isFunStore ? (
        <FunStoreMobileMenu
          regions={regions}
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
      ) : (
        <JinMobileMenu
          regions={regions}
          collections={collections}
          categories={categories}
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
}
